import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { BuildingIcon, CalendarIcon, SeatIcon } from "@/components/Icons";

const EXAMS = ["SSC CGL", "SSC CHSL", "IBPS PO", "IBPS Clerk", "SBI PO", "RBI Grade B", "Custom"];

export default function OwnerDashboard() {
    const [stats, setStats] = useState(null);
    const [centers, setCenters] = useState([]);
    const [slots, setSlots] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [bookingFilter, setBookingFilter] = useState("all");
    const [centerOpen, setCenterOpen] = useState(false);
    const [slotOpen, setSlotOpen] = useState(false);

    const filteredBookings = bookingFilter === "all"
        ? bookings
        : bookings.filter((b) => b.status === bookingFilter);

    const downloadSlotCsv = async (slot) => {
        try {
            const token = localStorage.getItem("es_token");
            const res = await fetch(`${API}/slots/${slot.id}/attendance.csv`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) throw new Error("Failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `attendance-${slot.date}-${slot.start_time.replace(":", "")}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Unable to download attendance CSV");
        }
    };

    const [cName, setCName] = useState("");
    const [cCity, setCCity] = useState("");
    const [cAddress, setCAddress] = useState("");
    const [cSeats, setCSeats] = useState(30);
    const [cRow, setCRow] = useState(8);
    const [cAmen, setCAmen] = useState("");
    const [cDesc, setCDesc] = useState("");
    const [cPhotoFiles, setCPhotoFiles] = useState([]);
    const [cPhotoPreviews, setCPhotoPreviews] = useState([]);
    const [uploadingPhotos, setUploadingPhotos] = useState(false);

    const [sCenter, setSCenter] = useState("");
    const [sExam, setSExam] = useState("SSC CGL");
    const [sDate, setSDate] = useState("");
    const [sStart, setSStart] = useState("10:00");
    const [sEnd, setSEnd] = useState("13:00");
    const [sSeats, setSSeats] = useState(20);
    const [sPrice, setSPrice] = useState(5);

    const refresh = () => {
        api.get("/owner/stats").then((r) => setStats(r.data));
        api.get("/centers/my").then((r) => setCenters(r.data));
        api.get("/slots/mine").then((r) => setSlots(r.data));
        api.get("/bookings/owner").then((r) => setBookings(r.data));
    };
    useEffect(refresh, []);

    const MAX_CENTER_PHOTOS = 3;

    const handlePhotoSelect = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        if (files.length > MAX_CENTER_PHOTOS) {
            toast.error(`You can upload up to ${MAX_CENTER_PHOTOS} photos only`);
        }
        const limited = files.slice(0, MAX_CENTER_PHOTOS);
        setCPhotoFiles(limited);
        setCPhotoPreviews(limited.map((f) => URL.createObjectURL(f)));
        e.target.value = "";
    };

    const removePhoto = (idx) => {
        setCPhotoFiles((prev) => prev.filter((_, i) => i !== idx));
        setCPhotoPreviews((prev) => prev.filter((_, i) => i !== idx));
    };

    const createCenter = async (e) => {
        e.preventDefault();
        try {
            let photoUrls = [];
            if (cPhotoFiles.length > 0) {
                setUploadingPhotos(true);
                const formData = new FormData();
                cPhotoFiles.forEach((f) => formData.append("files", f));
                const up = await api.post("/centers/upload-photos", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                photoUrls = up.data.urls;
                setUploadingPhotos(false);
            }
            await api.post("/centers", {
                name: cName, city: cCity, address: cAddress,
                total_seats: Number(cSeats), seats_per_row: Number(cRow),
                amenities: cAmen.split(",").map((s) => s.trim()).filter(Boolean),
                description: cDesc, photo_urls: photoUrls,
            });
            toast.success("Center submitted for approval");
            setCenterOpen(false);
            setCName(""); setCCity(""); setCAddress(""); setCSeats(30); setCAmen(""); setCDesc("");
            setCPhotoFiles([]); setCPhotoPreviews([]);
            refresh();
        } catch (err) {
            setUploadingPhotos(false);
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    const createSlot = async (e) => {
        e.preventDefault();
        if (!sCenter) { toast.error("Pick a center first"); return; }
        try {
            await api.post("/slots", {
                center_id: sCenter, exam_category: sExam, date: sDate,
                start_time: sStart, end_time: sEnd,
                total_seats: Number(sSeats), price: Number(sPrice),
            });
            toast.success("Slot added");
            setSlotOpen(false);
            setSDate(""); setSStart("10:00"); setSEnd("13:00"); setSSeats(20); setSPrice(5);
            refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-10">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline mb-2">Owner console</div>
                    <h1 className="font-heading text-4xl font-bold tracking-tight">Your centers</h1>
                </div>
                <div className="flex gap-2">
                    <Dialog open={centerOpen} onOpenChange={setCenterOpen}>
                        <DialogTrigger asChild>
                            <Button variant="outline" className="rounded-sm" data-testid="add-center-btn">+ Add center</Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm max-w-lg">
                            <DialogHeader><DialogTitle className="font-heading">New test center</DialogTitle></DialogHeader>
                            <form onSubmit={createCenter} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label>Name</Label><Input value={cName} onChange={(e) => setCName(e.target.value)} required data-testid="c-name" /></div>
                                    <div><Label>City</Label><Input value={cCity} onChange={(e) => setCCity(e.target.value)} required data-testid="c-city" /></div>
                                </div>
                                <div><Label>Address</Label><Input value={cAddress} onChange={(e) => setCAddress(e.target.value)} required data-testid="c-address" /></div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label>Total seats</Label><Input type="number" value={cSeats} onChange={(e) => setCSeats(e.target.value)} required data-testid="c-seats" /></div>
                                    <div><Label>Seats per row</Label><Input type="number" min={4} max={20} value={cRow} onChange={(e) => setCRow(e.target.value)} required data-testid="c-row" /></div>
                                </div>
                                <div><Label>Amenities (comma-separated)</Label><Input value={cAmen} onChange={(e) => setCAmen(e.target.value)} placeholder="AC, Wired Internet, Parking" data-testid="c-amen" /></div>
                                <div>
                                    <Label>Photos (up to 3)</Label>
                                    <Input
                                        type="file"
                                        accept="image/png, image/jpeg, image/webp"
                                        multiple
                                        onChange={handlePhotoSelect}
                                        data-testid="c-photo"
                                    />
                                    {cPhotoPreviews.length > 0 && (
                                        <div className="mt-2 flex gap-2">
                                            {cPhotoPreviews.map((src, idx) => (
                                                <div key={idx} className="relative w-16 h-16 border border-border overflow-hidden">
                                                    <img src={src} alt={`preview-${idx}`} className="w-full h-full object-cover" />
                                                    <button
                                                        type="button"
                                                        onClick={() => removePhoto(idx)}
                                                        className="absolute top-0 right-0 bg-black/70 text-white text-[10px] w-4 h-4 flex items-center justify-center"
                                                        data-testid={`c-photo-remove-${idx}`}
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div><Label>Description</Label><Textarea value={cDesc} onChange={(e) => setCDesc(e.target.value)} data-testid="c-desc" /></div>
                                <DialogFooter><Button type="submit" disabled={uploadingPhotos} className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="c-submit">{uploadingPhotos ? "Uploading…" : "Submit"}</Button></DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                    <Dialog open={slotOpen} onOpenChange={setSlotOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-sm bg-primary hover:bg-primary/90" data-testid="add-slot-btn">+ Add slot</Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm max-w-lg">
                            <DialogHeader><DialogTitle className="font-heading">New slot</DialogTitle></DialogHeader>
                            <form onSubmit={createSlot} className="space-y-3">
                                <div>
                                    <Label>Center</Label>
                                    <Select value={sCenter} onValueChange={setSCenter}>
                                        <SelectTrigger data-testid="s-center"><SelectValue placeholder="Choose center" /></SelectTrigger>
                                        <SelectContent>
                                            {centers.filter((c) => c.status === "approved").map((c) => (
                                                <SelectItem key={c.id} value={c.id}>{c.name} — {c.city}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    {centers.filter((c) => c.status === "approved").length === 0 && (
                                        <p className="text-xs text-muted-foreground mt-1">You need at least one approved center. Ask admin to approve.</p>
                                    )}
                                </div>
                                <div>
                                    <Label>Exam</Label>
                                    <Select value={sExam} onValueChange={setSExam}>
                                        <SelectTrigger data-testid="s-exam"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {EXAMS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div><Label>Date</Label><Input type="date" value={sDate} onChange={(e) => setSDate(e.target.value)} required data-testid="s-date" /></div>
                                    <div><Label>Start</Label><Input type="time" value={sStart} onChange={(e) => setSStart(e.target.value)} required data-testid="s-start" /></div>
                                    <div><Label>End</Label><Input type="time" value={sEnd} onChange={(e) => setSEnd(e.target.value)} required data-testid="s-end" /></div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div><Label>Seats for this slot</Label><Input type="number" value={sSeats} onChange={(e) => setSSeats(e.target.value)} required data-testid="s-seats" /></div>
                                    <div><Label>Price (INR)</Label><Input type="number" step="0.01" value={sPrice} onChange={(e) => setSPrice(e.target.value)} required data-testid="s-price" /></div>
                                </div>
                                <DialogFooter><Button type="submit" className="bg-primary hover:bg-primary/90 rounded-sm" data-testid="s-submit">Publish slot</Button></DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
                <Stat label="Centers" value={stats?.centers ?? "—"} />
                <Stat label="Slots" value={stats?.slots ?? "—"} />
                <Stat label="Bookings" value={stats?.bookings ?? "—"} />
                <Stat label="Confirmed" value={stats?.confirmed ?? "—"} />
            </div>

            <Tabs defaultValue="centers" className="mt-10">
                <TabsList className="rounded-sm bg-secondary">
                    <TabsTrigger value="centers" data-testid="tab-centers">Centers</TabsTrigger>
                    <TabsTrigger value="slots" data-testid="tab-slots">Slots</TabsTrigger>
                    <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings</TabsTrigger>
                </TabsList>

                <TabsContent value="centers">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>City</TableHead>
                                <TableHead>Seats</TableHead>
                                <TableHead>Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {centers.map((c) => (
                                <TableRow key={c.id} data-testid={`owner-center-${c.id}`}>
                                    <TableCell className="font-medium">{c.name}</TableCell>
                                    <TableCell>{c.city}</TableCell>
                                    <TableCell className="font-mono">{c.total_seats}</TableCell>
                                    <TableCell>
                                        <span className={`text-[10px] tracking-widest border px-1.5 py-0.5 ${
                                            c.status === "approved" ? "text-emerald-700 border-emerald-200 bg-emerald-50" :
                                            c.status === "rejected" ? "text-red-700 border-red-200 bg-red-50" :
                                            "text-amber-700 border-amber-200 bg-amber-50"
                                        }`}>{c.status.toUpperCase()}</span>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {centers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">No centers yet.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </TabsContent>

                <TabsContent value="slots">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>Time</TableHead>
                                <TableHead>Exam</TableHead>
                                <TableHead>Seats</TableHead>
                                <TableHead>Confirmed</TableHead>
                                <TableHead className="text-right">Attendance</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {slots.map((s) => {
                                const confirmedCount = bookings.filter((b) => b.slot_id === s.id && b.status === "confirmed").length;
                                return (
                                    <TableRow key={s.id} data-testid={`owner-slot-${s.id}`}>
                                        <TableCell className="font-mono">{s.date}</TableCell>
                                        <TableCell className="font-mono">{s.start_time}–{s.end_time}</TableCell>
                                        <TableCell>{s.exam_category}</TableCell>
                                        <TableCell className="font-mono">{s.total_seats}</TableCell>
                                        <TableCell className="font-mono">{confirmedCount}</TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                className="rounded-sm"
                                                disabled={confirmedCount === 0}
                                                onClick={() => downloadSlotCsv(s)}
                                                data-testid={`download-csv-${s.id}`}
                                            >
                                                CSV
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {slots.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No slots yet.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </TabsContent>

                <TabsContent value="bookings">
                    <div className="flex items-center gap-2 mb-3">
                        <label className="text-xs font-medium">Filter:</label>
                        <Select value={bookingFilter} onValueChange={setBookingFilter}>
                            <SelectTrigger className="w-40 rounded-sm" data-testid="booking-status-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                            </SelectContent>
                        </Select>
                        <span className="ml-auto text-xs text-muted-foreground font-mono" data-testid="booking-count">
                            {filteredBookings.length} result{filteredBookings.length === 1 ? "" : "s"}
                        </span>
                    </div>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Code</TableHead>
                                <TableHead>Student</TableHead>
                                <TableHead>Exam</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Seat</TableHead>
                                <TableHead>Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredBookings.map((b) => (
                                <TableRow key={b.id} data-testid={`owner-booking-${b.id}`}>
                                    <TableCell className="font-mono">{b.booking_code}</TableCell>
                                    <TableCell>{b.student_name}<div className="text-xs text-muted-foreground">{b.student_email}</div></TableCell>
                                    <TableCell>{b.exam_category}</TableCell>
                                    <TableCell className="font-mono">{b.exam_date} {b.exam_start_time}</TableCell>
                                    <TableCell className="font-mono">S-{String(b.seat_number).padStart(3, "0")}</TableCell>
                                    <TableCell>
                                        <span className={`text-[10px] tracking-widest border px-1.5 py-0.5 ${
                                            b.status === "confirmed" ? "text-emerald-700 border-emerald-200 bg-emerald-50" :
                                            b.status === "cancelled" ? "text-red-700 border-red-200 bg-red-50" :
                                            "text-amber-700 border-amber-200 bg-amber-50"
                                        }`}>{b.status.toUpperCase()}</span>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {filteredBookings.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No bookings match this filter.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border border-border bg-card p-5">
            <div className="overline">{label}</div>
            <div className="font-heading text-3xl font-bold mt-1">{value}</div>
        </div>
    );
}
